import type { OrganizationManager, User } from "@entities";
import {
  Autocomplete,
  Button,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { PARTNER_MANAGER_PHONE_LOCKED } from "@shared/entities/user/config";
import { Roles } from "@shared/Roles";
import { useEffect, useState } from "react";
import { remult } from "remult";
import { FormSection, LinkedRow } from "./partner-organization-form-ui";

type Props = {
  orgManagerSearch: string;
  onOrgManagerSearchChange: (value: string) => void;
  orgManagerOptions: OrganizationManager[];
  selectedOrgManager: OrganizationManager | null;
  onSelectedOrgManagerChange: (manager: OrganizationManager | null) => void;
  onLinkInternalManager: () => void;
  internalManagers: OrganizationManager[];
  onRemoveInternalManager: (manager: OrganizationManager) => void;
  managerPhone: string;
  onManagerPhoneChange: (value: string) => void;
  onLinkExistingManager: () => void;
  newManagerName: string;
  onNewManagerNameChange: (value: string) => void;
  newManagerPhone: string;
  onNewManagerPhoneChange: (value: string) => void;
  newManagerEmail: string;
  onNewManagerEmailChange: (value: string) => void;
  newManagerRole: string;
  onNewManagerRoleChange: (value: string) => void;
  onCreateManager: () => void;
  appManagers: User[];
  onRemoveAppManager: (user: User) => void;
  onUpdateAppManager: (
    user: User,
    patch: {
      name: string;
      phoneNumber: string;
      email: string;
      partnerContactRole: string;
    },
  ) => void;
};

function AppManagerEditor({
  manager,
  onSave,
  onRemove,
}: {
  manager: User;
  onSave: (patch: {
    name: string;
    phoneNumber: string;
    email: string;
    partnerContactRole: string;
  }) => void;
  onRemove: () => void;
}) {
  const [name, setName] = useState(manager.name ?? "");
  const [phoneNumber, setPhoneNumber] = useState(manager.phoneNumber ?? "");
  const [email, setEmail] = useState(manager.email ?? "");
  const [role, setRole] = useState(manager.partnerContactRole ?? "");
  const canEditPhone = remult.isAllowed(Roles.SuperAdmins);

  useEffect(() => {
    setName(manager.name ?? "");
    setPhoneNumber(manager.phoneNumber ?? "");
    setEmail(manager.email ?? "");
    setRole(manager.partnerContactRole ?? "");
  }, [manager.id, manager.name, manager.phoneNumber, manager.email, manager.partnerContactRole]);

  return (
    <Stack gap={1.25}>
      <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
        <TextField
          label="שם"
          value={name}
          onChange={(event) => setName(event.target.value)}
          fullWidth
        />
        <TextField
          label="טלפון"
          value={phoneNumber}
          onChange={(event) => setPhoneNumber(event.target.value)}
          disabled={!canEditPhone}
          helperText={canEditPhone ? undefined : PARTNER_MANAGER_PHONE_LOCKED}
          fullWidth
        />
        <TextField
          label="מייל"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          fullWidth
        />
        <TextField
          label="תפקיד"
          value={role}
          onChange={(event) => setRole(event.target.value)}
          fullWidth
        />
      </Stack>
      <Stack direction="row" gap={1}>
        <Button
          variant="contained"
          onClick={() =>
            onSave({
              name,
              phoneNumber,
              email,
              partnerContactRole: role,
            })
          }
          sx={{ textTransform: "none" }}
        >
          שמירת פרטים
        </Button>
        <Button color="error" onClick={onRemove} sx={{ textTransform: "none" }}>
          הסר מנהל אפליקציה
        </Button>
      </Stack>
    </Stack>
  );
}

export function PartnerOrganizationManagersPanel({
  orgManagerSearch,
  onOrgManagerSearchChange,
  orgManagerOptions,
  selectedOrgManager,
  onSelectedOrgManagerChange,
  onLinkInternalManager,
  internalManagers,
  onRemoveInternalManager,
  managerPhone,
  onManagerPhoneChange,
  onLinkExistingManager,
  newManagerName,
  onNewManagerNameChange,
  newManagerPhone,
  onNewManagerPhoneChange,
  newManagerEmail,
  onNewManagerEmailChange,
  newManagerRole,
  onNewManagerRoleChange,
  onCreateManager,
  appManagers,
  onRemoveAppManager,
  onUpdateAppManager,
}: Props) {
  return (
    <Stack gap={2.5}>
      <FormSection
        title="מנהלים ארגוניים"
        description="אופציונלי - נדרש כדי שמנהלי Admin/Manager יראו את הארגון. לא קשור לחיפוש מתנדבים."
      >
        <Stack gap={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            gap={1.5}
            alignItems="flex-start"
          >
            <Autocomplete
              sx={{ flex: 1, width: "100%" }}
              options={orgManagerOptions}
              value={selectedOrgManager}
              onChange={(_, manager) => onSelectedOrgManagerChange(manager)}
              inputValue={orgManagerSearch}
              onInputChange={(_, value) => onOrgManagerSearchChange(value)}
              getOptionLabel={(manager) =>
                `${manager.name} · ${manager.phoneNumber}${manager.organization?.name ? ` · ${manager.organization.name}` : ""}`
              }
              isOptionEqualToValue={(a, b) => a.id === b.id}
              noOptionsText="הקלד לפחות 2 תווים לחיפוש"
              renderInput={(params) => (
                <TextField {...params} label="חיפוש מנהל ארגון" />
              )}
            />
            <Button
              variant="contained"
              disabled={!selectedOrgManager}
              onClick={onLinkInternalManager}
              sx={{
                whiteSpace: "nowrap",
                textTransform: "none",
                height: 56,
                px: 2.5,
                borderRadius: 2,
              }}
            >
              שייך למערכת
            </Button>
          </Stack>
          {internalManagers.length === 0 ? (
            <Typography color="text.secondary" sx={{ fontSize: 14 }}>
              אין מנהלים ארגוניים משויכים
            </Typography>
          ) : (
            internalManagers.map((manager) => (
              <LinkedRow
                key={manager.id}
                primary={manager.name}
                secondary={`${manager.phoneNumber}${manager.organization?.name ? ` · ${manager.organization.name}` : ""}`}
                onRemove={() => onRemoveInternalManager(manager)}
                removeLabel="הסר מנהל מערכת"
              />
            ))
          )}
        </Stack>
      </FormSection>

      <FormSection
        title="מנהלי אפליקציה"
        description="משתמש weme-app - רואה משימות הארגון באזור האישי (OTP)."
      >
        <Stack gap={3}>
          <Stack gap={1.5}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              gap={1.5}
              alignItems="flex-start"
            >
              <TextField
                label="שיוך משתמש קיים לפי טלפון"
                value={managerPhone}
                onChange={(e) => onManagerPhoneChange(e.target.value)}
                placeholder="050-0000000"
                fullWidth
              />
              <Button
                variant="outlined"
                onClick={onLinkExistingManager}
                sx={{
                  whiteSpace: "nowrap",
                  textTransform: "none",
                  height: 56,
                  px: 2.5,
                  borderRadius: 2,
                }}
              >
                שייך
              </Button>
            </Stack>
          </Stack>

          <Stack
            direction={{ xs: "column", md: "row" }}
            gap={1.5}
            alignItems="flex-start"
          >
            <TextField
              label="שם מלא למנהל חדש"
              value={newManagerName}
              onChange={(e) => onNewManagerNameChange(e.target.value)}
              fullWidth
            />
            <TextField
              label="טלפון"
              value={newManagerPhone}
              onChange={(e) => onNewManagerPhoneChange(e.target.value)}
              placeholder="050-0000000"
              fullWidth
            />
            <TextField
              label="מייל"
              value={newManagerEmail}
              onChange={(e) => onNewManagerEmailChange(e.target.value)}
              fullWidth
            />
            <TextField
              label="תפקיד"
              value={newManagerRole}
              onChange={(e) => onNewManagerRoleChange(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={onCreateManager}
              sx={{
                whiteSpace: "nowrap",
                textTransform: "none",
                height: 56,
                px: 2.5,
                borderRadius: 2,
              }}
            >
              צור מנהל
            </Button>
          </Stack>

          {appManagers.length === 0 ? null : (
            <Stack gap={1.25}>
              {appManagers.map((manager) => (
                <AppManagerEditor
                  key={manager.id}
                  manager={manager}
                  onSave={(patch) => onUpdateAppManager(manager, patch)}
                  onRemove={() => onRemoveAppManager(manager)}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </FormSection>
    </Stack>
  );
}
